import ReCAPTCHA from "react-google-recaptcha"
import Personnummer from "personnummer"
import { useEffect, useRef, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import isEmail from "validator/lib/isEmail"
import isMobilePhone from "validator/lib/isMobilePhone"
import { eventSignUp } from "@/lib/api/events"
import { useMember } from "@/lib/hooks/useMember"
import getInputValidationClasses from "@/lib/helpers/getInputValidationClasses"
import { useAppContext, usePropsObject } from "@/lib/contexts/appContext"
import Block from "@/components/wrappers/block"
import { ErrorMessage } from "@/components/checkout-form/errorMessage"
import Button from "@/common/button/button"
import { InputDescription } from "@/common/form/input"
import { input } from "@/common/form/input.module.css"
import InputGroup from "@/common/form/inputGroup"
import Label from "@/common/form/label"
import styles from "./calendar-single.module.css"
import classNames from "classnames"

export default function EventForm({ block, settings }) {
  const siteKey = process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY
  const recaptchaRef = useRef()
  const { activityId } = usePropsObject()
  const { checkoutFormRef } = useAppContext()
  const { hasMounted, isLoggedIn, token, fullName } = useMember()
  const [useLoggedinUser, setUseLoggedinUser] = useState(false)
  const methods = useForm({
    mode: "onChange"
  })
  const {
    formState,
    control,
    register,
    handleSubmit,
    watch,
    setError,
    clearErrors,
    reset
  } = methods

  let { errors, isSubmitSuccessful, isSubmitting, isValid } = formState

  const pin = watch("pin")
  const firstName = watch("firstName")
  const lastName = watch("lastName")
  const email = watch("email")
  const phone = watch("phone")
  const comment = watch("comment")

  useEffect(() => {
    clearErrors("form")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, firstName, lastName, email, phone, comment])

  useEffect(() => {
    if (hasMounted && isLoggedIn) {
      setUseLoggedinUser(true)
    }
  }, [hasMounted, isLoggedIn])

  const onSubmit = async ({
    firstName,
    lastName,
    email,
    phone,
    comment,
    pin
  }) => {
    clearErrors()
    const captcha = await recaptchaRef.current.executeAsync()

    if (pin.length > 0) {
      const instance = Personnummer.parse(pin)
      pin = instance.format(true)
    }

    const response = await eventSignUp({
      firstName,
      lastName,
      email,
      phone,
      comment,
      activityId,
      pin,
      captcha,
      token
    })

    if (!response?.success || !response) {
      const errorMessage = response?.message
        ? response.message
        : "Ett fel uppstod, det går inte att anmäla sig just nu."

      setError("form", {
        type: "manual",
        message: errorMessage
      })
    } else {
      if (settings.haveSeats) {
        settings.getSeats(activityId)
      }
      reset()
    }
  }

  const submitDisabled =
    (!isValid && !useLoggedinUser) || isSubmitting || isSubmitSuccessful

  return (
    <Block
      width="bleed"
      gap={block?.attrs?.preview ? "none" : "small"}
      block={block}
      className={styles.form}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} id="form" ref={checkoutFormRef}>
          <CurrentStatus show={false} />
          <ReCAPTCHA
            className="hidden"
            ref={recaptchaRef}
            size="invisible"
            sitekey={siteKey}
          />
          <div className="inner-wrapper">
            <h2 className="font-bold text-2.5xl font-sans">Anmälan</h2>
            <FormFields
              {...{
                register,
                control,
                hasMounted,
                isLoggedIn,
                fullName,
                useLoggedinUser,
                setUseLoggedinUser
              }}
            />
            <InputGroup>
              <Label name="comment">Övrig information:</Label>
              <textarea
                className={[
                  input,
                  getInputValidationClasses("comment", control)
                ].join(" ")}
                id="comment"
                {...register("comment")}
              />
              <InputDescription>
                Ange eventuellt behov, önskemål eller ställ en fråga till
                arrangören
              </InputDescription>
            </InputGroup>
            <div
              className="mb-6 text-sm md:text-base wysiwyg-content"
              dangerouslySetInnerHTML={{
                __html: block.attrs?.terms
              }}></div>
            <div className="text-center">
              <Button
                type="submit"
                ariaLabel={
                  isSubmitSuccessful
                    ? "Tack för din anmälan!"
                    : isSubmitting
                    ? "Skickar anmälan..."
                    : "Skicka anmälan"
                }
                loading={isSubmitting}
                disabled={submitDisabled}
                theme="lime">
                {isSubmitSuccessful
                  ? "Tack för din anmälan!"
                  : isSubmitting
                  ? "Skickar anmälan..."
                  : "Skicka anmälan"}
              </Button>
              {isSubmitSuccessful && block?.attrs?.message && (
                <>
                  <div className="mt-3 text-base text-ink-50">
                    {block.attrs.message}
                  </div>
                </>
              )}
              {errors?.form && (
                <div className="mt-3 text-xs text-red-100">
                  {errors.form.message}
                </div>
              )}
            </div>
          </div>
        </form>
      </FormProvider>
    </Block>
  )
}

const FormFields = ({
  register,
  control,
  hasMounted,
  isLoggedIn,
  useLoggedinUser,
  setUseLoggedinUser,
  fullName
}) => {
  if (hasMounted && isLoggedIn && useLoggedinUser) {
    return (
      <div className="logged-in-as">
        <strong>Inloggad som: </strong>
        <span>{fullName}</span>
        <a
          className="toggle-form-fields"
          onClick={() => setUseLoggedinUser(false)}>
          Anmäl någon annan
        </a>
      </div>
    )
  }

  return (
    <>
      <InputGroup>
        {hasMounted && isLoggedIn && !useLoggedinUser && (
          <a
            className="toggle-form-fields"
            onClick={() => setUseLoggedinUser(true)}>
            Anmäl inloggad medlem
          </a>
        )}
        <Label name="pin">Personnummer:</Label>
        <input
          className={classNames([
            input,
            getInputValidationClasses("pin", control)
          ])}
          id="pin"
          {...register("pin", {
            validate: (value) =>
              !value ||
              Personnummer.valid(value) ||
              "Ange ett giltigt personnummer"
          })}
        />
        <ErrorMessage name="pin" />
        <InputDescription>
          Anges för aktiviteter som kräver medlemsskap.
        </InputDescription>
      </InputGroup>

      <InputGroup>
        <Label name="firstName" required={true}>
          Förnamn:
        </Label>
        <input
          className={[
            input,
            getInputValidationClasses("firstName", control)
          ].join(" ")}
          id="firstName"
          {...register("firstName", {
            required: true
          })}
        />
        <ErrorMessage name="firstName" />
      </InputGroup>

      <InputGroup>
        <Label name="lastName" required={true}>
          Efternamn:
        </Label>
        <input
          className={[
            input,
            getInputValidationClasses("lastName", control)
          ].join(" ")}
          id="lastName"
          {...register("lastName", {
            required: true
          })}
        />
        <ErrorMessage name="lastName" />
      </InputGroup>

      <InputGroup>
        <Label name="email" required={true}>
          E-post:
        </Label>
        <input
          className={[input, getInputValidationClasses("email", control)].join(
            " "
          )}
          id="email"
          {...register("email", {
            required: true,
            validate: (value) =>
              isEmail(value) || "Ange en giltig e-postadress."
          })}
        />
        <ErrorMessage name="email" />
      </InputGroup>

      <InputGroup>
        <Label name="phone">Telefon (valfritt):</Label>
        <input
          className={[input, getInputValidationClasses("phone", control)].join(
            " "
          )}
          id="phone"
          {...register("phone", {
            validate: (value) =>
              !value ||
              isMobilePhone(value, "sv-SE") ||
              "Ange ett giltigt mobiltelefonnummer."
          })}
        />
        <ErrorMessage name="phone" />
      </InputGroup>
    </>
  )
}

const CurrentStatus = ({ show }) => {
  if (!show) {
    return
  }
  return (
    <div className="current-status">
      <span>Just nu</span>
      <h4>Just nu kan vi inte ta emot anmälan till Aktiviteter.</h4>
      <p>
        Vi utför underhållsarbete av systemet 2023-12-14. Välkommen åter
        2023-12-15.
      </p>
    </div>
  )
}
